import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { cloneDeep, isEmpty, orderBy } from 'lodash';
import clsx from 'clsx';
import EsmsDialog from '../../components/EsmsDialog';
import { filterBy } from '@progress/kendo-data-query';
import { ListBox, ListBoxToolbar, processListBoxData, processListBoxDragAndDrop } from '@progress/kendo-react-listbox';
import FormControl from '@material-ui/core/FormControl';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputLabel from '@material-ui/core/InputLabel';
import * as BasAgentActions from '../../actions/basAgentActions';
import * as K2ACoreActions from '../../actions/k2aCoreActions';
import * as ApplicationMessageActions from '../../actions/applicationMessageActions';

import * as classes from './AgentLinkSiteModal.module.css';
import { InputAdornment } from '@material-ui/core';
import { Search } from '@material-ui/icons';

const SELECTED_FIELD = 'selected';

const intitialFilter = {
    logic:   'and', 
    filters: [
        { field:    'client.name', 
            operator: 'contains', 
            value:    ''}, 
        { field:    'name', 
            operator: 'contains', 
            value:    ''}
    ]
};

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const AgentLinkSiteModal = ({
    agentId,
    clearErrors,
    close,
    companyInfo,
    errors,
    fetchAgentById,
    fetchCoreSites,
    isLoading,
    linkAgentsToSites,
    open,
    sites,
    title,
    unlinkAgentsFromSites,
    ...props
}) => {
    const [state, setState] = useState({
        linkedSites:            [],
        notLinkedSites:         [],
        originalLinkedSites:    [],
        originalNotLinkedSites: [],
        draggedItem:            {},
        linkedFilter:           intitialFilter,
        unlinkedFilter:         intitialFilter
    });
    const [companyId, setCompanyId] = useState('');
    const [assignedSites, setAssignedSites] = useState([]);
    const [isLoadingAgent, setIsLoadingAgent] = useState(false);
    const [isLoadingSites, setIsLoadingSites] = useState(false);
    const loadingLinkedSitesDataSet = [{name: 'Loading Linked Sites...'}];
    const loadingSiteListDataSet = [{name: 'Loading Site List...'}];

    const CustomListItem = props => {
        let { dataItem, selected, className, ...others } = props;
        return (
            <li className={selected ? clsx(className, classes.selectedListItem) : className} {...others} style={{}}>
                <div>
                    <span className={classes.ListItem}><b>{`Client: ${dataItem.client ? dataItem.client.name : 'N/A'}`}</b></span>
                    <br />
                    <span className={classes.ListItem}>{`Site: ${dataItem.name}`}</span>
                </div>
            </li>
        );
    };

    useEffect(() => {
        return () => {
            clearErrors();
        };
    }, []);

    useEffect(() => {
        const init = async () => {
            setIsLoadingAgent(true);
            const selectedAgent = await fetchAgentById(agentId);
            setAssignedSites(selectedAgent.site?.filter(s => s.id !== EMPTY_GUID));
            setIsLoadingAgent(false);
        };
        if (open && agentId !== '') init();
    }, [agentId, open]);

    useEffect(() => {
        const init = async () => {
            setCompanyId(companyInfo.companyId);
            if (isEmpty(sites)) {
                setIsLoadingSites(true);
                await fetchCoreSites(companyInfo.companyId);
                setIsLoadingSites(false);
            }
        };

        if (companyInfo && companyInfo.companyId) init();

    }, [companyInfo]);

    useEffect(() => {
        if (!isEmpty(sites)) {
            const siteList = sites.map(s => {
                s.selected = false;
                return s;
            });

            if (isEmpty(assignedSites)) {
                setState({
                    ...state,
                    notLinkedSites:         cloneDeep(siteList),
                    originalNotLinkedSites: cloneDeep(siteList),
                    linkedSites:            [],
                    originalLinkedSites:    []
                });
            }
            else {
            // the passed in list of assigned sites does not have the client information
                const updatedLinkedSites = cloneDeep(assignedSites.map((s) => {
                    if (!isEmpty(sites)) {
                        const foundSite = sites?.find(site => site.id === s.id);
                        if (foundSite) {
                            foundSite.selected = false;
                            return foundSite;
                        }
                        else {
                            return s;
                        }
                    }
                    else return s;
                }));
                // remove linked sites from the unlinked list
                const notLinkedList = sites.filter(s => !assignedSites.some(as => as.id === s.id));
                setState({
                    ...state,
                    notLinkedSites:         notLinkedList,
                    originalNotLinkedSites: notLinkedList,
                    linkedSites:            updatedLinkedSites,
                    originalLinkedSites:    updatedLinkedSites
                });
            }
        }
    }, [sites, assignedSites]);

    const handleClose = () => {
        setState({
            ...state,
            linkedSites:            [],
            originalLinkedSites:    [],
            notLinkedSites:         [],
            originalNotLinkedSites: []
        });        
        clearErrors();
        close();
    };

    const onHandleItemClick = (event, data, connectedData) => {
        setState({
            ...state,
            [data]: state[data].map(item => {
                if (item.id === event.dataItem.id) {
                    item[SELECTED_FIELD] = !item[SELECTED_FIELD];
                }
                else if (!event.nativeEvent.ctrlKey) {
                    item[SELECTED_FIELD] = false;
                }
                return item;
            }),
            [connectedData]: state[connectedData].map(item => {
                item[SELECTED_FIELD] = false;
                return item;
            })
        });
    };

    const handleToolBarClick = e => {
        let toolName = e.toolName;
        let result = processListBoxData(state.notLinkedSites, state.linkedSites, toolName, SELECTED_FIELD);
        setState({
            ...state,
            notLinkedSites: result.listBoxOneData,
            linkedSites:    result.listBoxTwoData
        });
    };

    const onDragStart = e => {
        setState({
            ...state,
            draggedItem: e.dataItem
        });
    };

    const onDrop = e => {
        let result = processListBoxDragAndDrop(state.notLinkedSites, state.linkedSites, state.draggedItem, e.dataItem, 'id');
        setState({
            ...state,
            notLinkedSites: result.listBoxOneData,
            linkedSites:    result.listBoxTwoData
        });
    };

    const handleSubmit = async () => {
        let sitesLinked = true;
        let sitesUnlinked = true;
        const sitesToLink = state.linkedSites.filter(s => !state.originalLinkedSites.some(as => as.id === s.id));
        const sitesToUnlink = state.notLinkedSites.filter(s => !state.originalNotLinkedSites.some(as => as.id === s.id));

        const linkIds = sitesToLink.map(s => s.id);
        const unlinkIds = sitesToUnlink.map(s => s.id);

        if (!isEmpty(linkIds)) {
            sitesLinked = await linkAgentsToSites(companyId, agentId, linkIds);
        }

        if (!isEmpty(unlinkIds)) {
            sitesUnlinked = await unlinkAgentsFromSites(companyId, agentId, unlinkIds);
        }

        if (sitesLinked && sitesUnlinked) {
            close();
        }
    };

    const handleFilterChange = prop => event => {
        if (prop === 'unlinked') {
            setState({
                ...state,
                unlinkedFilter: {
                    logic:   'or', 
                    filters: [
                        { field:    'client.name', 
                            operator: 'contains', 
                            value:    event.target.value}, 
                        { field:    'name', 
                            operator: 'contains', 
                            value:    event.target.value}
                    ]}
            });
        }
        else {
            setState({
                ...state,
                linkedFilter: {
                    logic:   'or', 
                    filters: [
                        { field:    'client.name', 
                            operator: 'contains', 
                            value:    event.target.value}, 
                        { field:    'name', 
                            operator: 'contains', 
                            value:    event.target.value}
                    ]}
            });
        }
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onConfirm={handleSubmit}
            disabled={isEmpty(state.linkedSites)}
            isLoading={isLoading}
            confirmText="Update Site Links"
            className={classes.modal}
        >
            <React.Fragment>
                <div className={classes.Container}>
                    <div className={clsx(classes.Row, classes.JustifyCenterContent)}>
                        <div className={classes.LeftColumn}>
                            <h4>Available Sites</h4>
                            <FormControl
                                classes={{
                                    root: classes.formControlRoot
                                }}
                                variant="outlined" 
                                size="small"
                                style={{marginBottom: '10px'}}
                            >
                                <InputLabel htmlFor="unlinkFilterBox">Search unlinked sites</InputLabel>
                                <OutlinedInput
                                    id="unlinkedFilterBox"
                                    variant="outlined"
                                    size="small"
                                    onChange={handleFilterChange('unlinked')}
                                    endAdornment={
                                        <InputAdornment
                                            position="end"
                                        >
                                            <Search />
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>
                            <ListBox
                                style={{
                                    height: '400px',
                                    width:  '100%'
                                }}
                                data={!isLoadingSites ? filterBy(orderBy(state.notLinkedSites, ['client.name', 'name']), state.unlinkedFilter) || [] : loadingSiteListDataSet}
                                textField={'name'}
                                item={CustomListItem}
                                selectedField={SELECTED_FIELD}
                                onItemClick={(e) => onHandleItemClick(e, 'notLinkedSites', 'linkedSites')}
                                onDragStart={onDragStart}
                                onDrop={onDrop}
                                toolbar={() => {
                                    return (
                                        <ListBoxToolbar
                                            tools={['transferTo', 'transferFrom', 'transferAllTo', 'transferAllFrom']} 
                                            data={state.notLinkedSites} 
                                            dataConnected={state.linkedSites}
                                            onToolClick={handleToolBarClick}
                                        />
                                    );
                                }}
                            />
                        </div>
                        <div className={classes.RightColumn}>
                            <h4>Linked Sites</h4>
                            <FormControl
                                fullWidth variant="outlined" 
                                size="small"
                                style={{marginBottom: '10px'}}
                            >
                                <InputLabel htmlFor="linkFilterBox">Search linked sites</InputLabel>
                                <OutlinedInput
                                    id="linkedFilterBox"
                                    variant="outlined"
                                    size="small"
                                    onChange={handleFilterChange('linked')}
                                    endAdornment={
                                        <InputAdornment
                                            position="end"
                                        >
                                            <Search />
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>
                            <ListBox 
                                style={{
                                    height: '400px',
                                    width:  '100%'
                                }}
                                data={!isLoadingAgent ? filterBy(orderBy(state.linkedSites, ['client.name', 'name']), state.linkedFilter) || [] : loadingLinkedSitesDataSet}
                                textField={'name'}
                                item={!isLoadingAgent ? CustomListItem : null}
                                selectedField={SELECTED_FIELD}
                                onItemClick={(e) => onHandleItemClick(e, 'linkedSites', 'notLinkedSites')}
                                onDragStart={onDragStart}
                                onDrop={onDrop}
                            />
                        </div>
                    </div>
                </div>
            </React.Fragment>
        </EsmsDialog>
    );
};

const mapStateToProps = state => {
    return {
        companyInfo: state.companyInfo,
        errors:      state.messages.errors,
        isLoading:   state.ui.isLoading,
        sites:       state.k2aCore.sites
    };
};

const mapDispatchToProps = {
    ...ApplicationMessageActions,
    ...BasAgentActions,
    ...K2ACoreActions
};

export default connect(mapStateToProps, mapDispatchToProps)(AgentLinkSiteModal);
